// PushFailureBanner
// -----------------
// A self-healing system-health banner shown to supervisor-and-above users (the
// admin "Supervisor" role + SuperUser) on every internal screen while one or more
// Sales Orders have permanently FAILED to push to Unleashed (status 'failed', not
// api_excluded). It re-queries the count on mount and whenever the active path
// changes (pathKey, passed from AppShell), so it clears automatically once the
// failures are resolved / released / excluded — no Edge-Function → banner coupling.
//
// Gated to isAdminRole, so a shelter caseworker never sees it (and the count query
// never even runs for them).

import { useEffect, useState } from "react";
import { countFailedPushes, getPushAlertFlags } from "../data/salesOrderPushes";
import { isAdminRole } from "./access";
import { useSession } from "./SessionContext.jsx";

export function PushFailureBanner({ pathKey, onNavigate }) {
  const { user } = useSession();
  const gated = isAdminRole(user);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!gated) {
      setCount(0);
      return;
    }
    let alive = true;
    (async () => {
      // The banner channel can be turned off on System Setup — respect it before
      // even counting.
      const flags = await getPushAlertFlags();
      if (!alive) return;
      if (!flags.banner) {
        setCount(0);
        return;
      }
      const n = await countFailedPushes();
      if (alive) setCount(n);
    })();
    return () => {
      alive = false;
    };
  }, [gated, pathKey]);

  if (!gated || count <= 0) return null;

  return (
    <div
      role="alert"
      style={{
        background: "#C62828",
        color: "white",
        textAlign: "center",
        padding: "12px 20px",
        fontSize: 14,
        fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 16,
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
      }}
    >
      <span style={{ fontWeight: 700 }}>
        ⚠️ {count} Sales Order{count === 1 ? "" : "s"} failed to push to Unleashed.
      </span>
      <button
        type="button"
        onClick={() => onNavigate("sales_orders")}
        style={{
          background: "white",
          color: "#C62828",
          border: "none",
          borderRadius: 6,
          padding: "6px 16px",
          fontSize: 13,
          cursor: "pointer",
          fontWeight: 700,
          fontFamily: "inherit",
        }}
      >
        Review Sales Orders
      </button>
    </div>
  );
}
